// ----------------------------------------------------------------------------
// Section: Site Header
// ----------------------------------------------------------------------------
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------
import { gsap } from "gsap";

// Scripts
import 'snippets/announcement-bar/announcement-bar';
import 'snippets/menu/menu';
import 'snippets/header-menu/header-menu';
import 'snippets/country-language-switcher/country-language-switcher';
import 'snippets/search-form/search';
import 'snippets/predictive-search/predictive-search';
import 'snippets/icon/icon';

// Styles
import './_header.scss';

// Optional Features
import './header--sticky';


// -------------------------------------
// Variables
// -------------------------------------

// Selectors
const HEADER_SELECTOR            = '.header';
const HEADER_CONTAINER_SELECTOR  = '.header__container';
const HAMBURGER_CLASS            = 'header__link--menu';
const HAMBURGER_SELECTOR         = '.header__link--menu';
const MENU_WRAPPER_SELECTOR      = '.header__nav-wrapper';
const MENU_ITEM_SELECTOR         = '.header-menu__item--dropdown';
const MENU_BUTTON_SELECTOR       = '.header-menu__button';
const HEADER_BUMP_SELECTOR       = '.header-bump';
const HEADER_BUMP_PATH_SELECTOR  = '.header-bump path';
const HEADER_BUMP_LOCK_SELECTOR  = '.js--bump-lock';

// Classes
const OPEN_CLASS                = 'js--is-open';
const NO_SCROLL_CLASS           = 'noscroll';
const HEADER_BUMP_LOCK_CLASS    = 'js--bump-lock';


// -------------------------------------
// Helper functions
// -------------------------------------

// --------------- Menu ----------------

// Open the menu
function openMenu(menu) {
    menu.$body.classList.add(NO_SCROLL_CLASS);
    menu.$header.classList.add(OPEN_CLASS);
    menu.$hamburger.classList.add(OPEN_CLASS);
    menu.$hamburger.setAttribute('aria-expanded', 'true');
}

// Close the menu
function closeMenu(menu) {
    menu.$body.classList.remove(NO_SCROLL_CLASS);
    menu.$header.classList.remove(OPEN_CLASS);
    menu.$hamburger.classList.remove(OPEN_CLASS);
    menu.$hamburger.setAttribute('aria-expanded', 'false');
    removeClassFromElements(menu.$items, OPEN_CLASS);
    setAttributeForElements(menu.$buttons, 'aria-expanded', 'false');
}

// Remove a given class from a list of elements
function removeClassFromElements(elems, className) {
    elems.forEach(function(elem, index) {
        elem.classList.remove(className);
    });
}

// Set the value of a given attribute for a list of elements
function setAttributeForElements(elems, attribute, value) {
    elems.forEach(function(elem) {
        elem.setAttribute(attribute, value);
    });
}


// --------------- Header Bump / Gooey Drip Animation ----------------

// Reference: https://gsap.com/docs/v3/GSAP/gsap.to()
// On Mouse Over & Mouse Move
function animateHeaderBumpOnMouseMove(event) {
    // Animation for the header bump path
    gsap.to(HEADER_BUMP_PATH_SELECTOR, {
        attr: {d: 'M180 0 C240 0 240 14 280 14 C320 14 320 0 380 0 Z'},
        svgOrigin: '200 0',
        transformOrigin: '50% top', // transformation will originate from the horizontal center of the element (50%) and the top edge of the element
        duration: 2.8,
        ease: 'back.out(0.9)',
    });

    // Animation for the header bump element
    gsap.to(HEADER_BUMP_SELECTOR, {
        left: event.pageX,
        duration: 0.8,
        ease: 'back.out(0.9)',
    });
}

// On Mouse Leave
function resetHeaderBumpOnMouseLeave() {
    gsap.defaults({
        overwrite: true
    });

    gsap.to(HEADER_BUMP_PATH_SELECTOR, {
        attr: {d: 'M180 0 C240 0 240 0 280 0 C320 0 320 0 380 0 Z'},
        duration: 0.8,
        ease: 'back.out(0.9)',
    });
}

// Lock the SVG to a specific position
function animateHeaderBumpLock(position) {
    gsap.defaults({
        overwrite: true
    });

    gsap.to(HEADER_BUMP_SELECTOR, {
        left: position,
        duration: 0.8,
        ease: 'back.out(0.9)',
    });
}

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', function() {
    const menu = {
        $body     : document.querySelector('body'),
        $header   : document.querySelector(HEADER_SELECTOR),
        $hamburger: document.querySelector(HAMBURGER_SELECTOR),
        $items    : document.querySelectorAll(MENU_ITEM_SELECTOR),
        $buttons  : document.querySelectorAll(MENU_BUTTON_SELECTOR)
    };

    // Toggle the mobile menu when the hamburger is clicked
    // Otherwise, close the menu if clicked outside of it
    menu.$body.addEventListener('click', function(event) {
        const $this = event.target;

        let hamburgerClick = false;
        let hamburger      = null;

        if ($this.classList.contains(HAMBURGER_CLASS)) {
            hamburgerClick = true;
            hamburger = $this;
        } else {
            const closestHamburger = $this.closest(HAMBURGER_SELECTOR);

            if (closestHamburger !== null) {
                hamburgerClick = true;
                hamburger = closestHamburger;
            }
        }

        if (hamburgerClick) {
            event.preventDefault();

            if (hamburger.classList.contains(OPEN_CLASS)) {
                closeMenu(menu);
            } else {
                openMenu(menu);
            }
        } else if ($this.closest(MENU_WRAPPER_SELECTOR) === null) {
            closeMenu(menu);
        }
    });


    // Header Bump animation
    const header = document.querySelector(HEADER_SELECTOR);
    const headerContainer = document.querySelector(HEADER_CONTAINER_SELECTOR);

    // Animation on mouse move
    header.addEventListener('mousemove', function (event) {
        if (!header.classList.contains(HEADER_BUMP_LOCK_CLASS)) {
            animateHeaderBumpOnMouseMove(event);
        }
    });

    // Animation on mouse leave
    header.addEventListener('mouseleave', function () {
        if (!header.classList.contains(HEADER_BUMP_LOCK_CLASS)) {
            resetHeaderBumpOnMouseLeave();
        }
    });

    // Create a MutationObserver
    const observer = new MutationObserver((mutationsList, observer) => {
        mutationsList.forEach((mutation) => {
            // Check if the mutation type is "attributes" (class attribute change)
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const targetElement = headerContainer.querySelector(HEADER_BUMP_LOCK_SELECTOR);

                // Check if the added class is the one you're interested in
                if (targetElement) {
                    const elemPositionX = targetElement.offsetLeft + targetElement.offsetWidth / 2;

                    // Call the desired function and pass the target element
                    animateHeaderBumpLock(elemPositionX);
                }
            }
        });
    });

    // Configure the observer to listen for attribute changes in the subtree
    observer.observe(headerContainer, { attributes: true, subtree: true });
});
